import { useState, useEffect } from 'react';

interface NotificationConfig {
  notificationEmail: string;
  notifyOnNew: boolean;
  notifyOnReply: boolean;
  emailSignature: string;
  autoReplyEnabled: boolean;
  autoReplyMessage: string;
}

const DEFAULT_CONFIG: NotificationConfig = {
  notificationEmail: '',
  notifyOnNew: true,
  notifyOnReply: false,
  emailSignature: 'Best regards,\nGreenly Electronics Recycling Team\n📞 (323) 555-0100\n🌐 greenlyrecycling.com',
  autoReplyEnabled: false,
  autoReplyMessage: 'Thank you for contacting Greenly Electronics Recycling! We have received your inquiry and will get back to you within 1 business day.',
};

const STORAGE_KEY = 'greenly_notification_config';

const NotificationSettings = () => {
  const [config, setConfig] = useState<NotificationConfig>(DEFAULT_CONFIG);
  const [saved, setSaved] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [activeSection, setActiveSection] = useState<'email' | 'alerts' | 'signature'>('email');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setConfig({ ...DEFAULT_CONFIG, ...JSON.parse(stored) });
      } catch {
        // ignore
      }
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleTestEmail = () => {
    if (!config.notificationEmail) return;
    const subject = encodeURIComponent('[Test] Greenly Admin Notification Test');
    const body = encodeURIComponent(
      `Hi,\n\nThis is a test notification from your Greenly Admin Dashboard.\n\nYour notification email is correctly configured to: ${config.notificationEmail}\n\nYou will receive new inquiry alerts at this address.\n\n${config.emailSignature}`
    );
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(config.notificationEmail)}&su=${subject}&body=${body}`,
      '_blank'
    );
    setTestSent(true);
    setTimeout(() => setTestSent(false), 4000);
  };

  const sections = [
    { key: 'email' as const, label: 'Gmail Address', icon: 'ri-mail-settings-line' },
    { key: 'alerts' as const, label: 'Alert Preferences', icon: 'ri-notification-3-line' },
    { key: 'signature' as const, label: 'Email Signature', icon: 'ri-quill-pen-line' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      {/* Sidebar nav */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-xl p-3 space-y-1">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors text-left ${
                activeSection === s.key
                  ? 'bg-[#1B6B3A] text-white'
                  : 'text-gray-600 hover:bg-[#F4FAF7] hover:text-[#1B6B3A]'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={s.icon}></i>
              </div>
              {s.label}
            </button>
          ))}
        </div>

        {/* Current config summary */}
        <div className="bg-white rounded-xl p-4 mt-4">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Current Config</div>
          <div className="space-y-2.5">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`text-xs ${config.notificationEmail ? 'ri-checkbox-circle-fill text-[#1B6B3A]' : 'ri-close-circle-fill text-red-400'}`}></i>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-700">Notification Email</div>
                <div className="text-xs text-gray-400 truncate max-w-[140px]">
                  {config.notificationEmail || 'Not configured'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`text-xs ${config.notifyOnNew ? 'ri-checkbox-circle-fill text-[#1B6B3A]' : 'ri-close-circle-fill text-gray-300'}`}></i>
              </div>
              <div className="text-xs text-gray-600">New inquiry alerts</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`text-xs ${config.notifyOnReply ? 'ri-checkbox-circle-fill text-[#1B6B3A]' : 'ri-close-circle-fill text-gray-300'}`}></i>
              </div>
              <div className="text-xs text-gray-600">Reply confirmations</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:col-span-3 space-y-4">

        {/* Gmail Address Section */}
        {activeSection === 'email' && (
          <div className="bg-white rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                <i className="ri-mail-settings-line text-[#1B6B3A] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Gmail Notification Address</h3>
                <p className="text-sm text-gray-400">New inquiry alerts will be sent to this Gmail address</p>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Gmail Address <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-google-line text-[#EA4335] text-base"></i>
                </div>
                <input
                  type="email"
                  value={config.notificationEmail}
                  onChange={(e) => setConfig({ ...config, notificationEmail: e.target.value })}
                  placeholder="yourname@gmail.com or yourname@yourdomain.com"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 transition-colors"
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Works with any Gmail or Google Workspace address. When a new inquiry arrives, you&apos;ll get an alert at this address.
              </p>
            </div>

            {/* How it works */}
            <div className="bg-[#F4FAF7] rounded-xl p-4 mb-5">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <i className="ri-information-line text-[#1B6B3A]"></i> How Gmail Notifications Work
              </div>
              <div className="space-y-2.5">
                {[
                  { icon: 'ri-mail-send-line', text: 'When a visitor submits a contact form, you\'ll receive an alert email at your configured Gmail address' },
                  { icon: 'ri-reply-line', text: 'Click "Reply via Gmail" on any inquiry to open Gmail with the customer\'s email pre-filled' },
                  { icon: 'ri-google-line', text: 'All replies are sent directly from your Gmail account — no third-party email service needed' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#1B6B3A] text-sm`}></i>
                    </div>
                    <p className="text-sm text-gray-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Test button */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleTestEmail}
                disabled={!config.notificationEmail}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                  config.notificationEmail
                    ? 'bg-[#EA4335] text-white hover:bg-[#c5372b]'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-google-line"></i>
                {testSent ? 'Test Email Opened in Gmail!' : 'Send Test Email via Gmail'}
              </button>
              {testSent && (
                <span className="text-sm text-[#1B6B3A] flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-fill"></i> Gmail compose window opened
                </span>
              )}
            </div>
          </div>
        )}

        {/* Alert Preferences Section */}
        {activeSection === 'alerts' && (
          <div className="bg-white rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                <i className="ri-notification-3-line text-[#1B6B3A] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Alert Preferences</h3>
                <p className="text-sm text-gray-400">Choose when you want to receive Gmail notifications</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                {
                  key: 'notifyOnNew' as keyof NotificationConfig,
                  label: 'New Inquiry Alerts',
                  description: 'Get notified via Gmail whenever a new contact form is submitted',
                  icon: 'ri-mail-unread-line',
                  recommended: true,
                },
                {
                  key: 'notifyOnReply' as keyof NotificationConfig,
                  label: 'Reply Confirmation',
                  description: 'Receive a copy of your reply when you respond to an inquiry',
                  icon: 'ri-reply-line',
                  recommended: false,
                },
                {
                  key: 'autoReplyEnabled' as keyof NotificationConfig,
                  label: 'Auto-Reply to Customers',
                  description: 'Automatically open Gmail to send a confirmation to customers after they submit a form',
                  icon: 'ri-robot-line',
                  recommended: false,
                },
              ].map((pref) => (
                <div
                  key={pref.key}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                    config[pref.key] ? 'border-[#1B6B3A]/30 bg-[#F4FAF7]' : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${config[pref.key] ? 'bg-[#E8F5EE]' : 'bg-white'}`}>
                    <i className={`${pref.icon} text-lg ${config[pref.key] ? 'text-[#1B6B3A]' : 'text-gray-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-gray-800">{pref.label}</span>
                      {pref.recommended && (
                        <span className="text-xs bg-[#1B6B3A] text-white px-2 py-0.5 rounded-full font-medium">Recommended</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{pref.description}</p>
                  </div>
                  <button
                    onClick={() => setConfig({ ...config, [pref.key]: !config[pref.key] })}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 mt-1 ${
                      config[pref.key] ? 'bg-[#1B6B3A]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        config[pref.key] ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    ></span>
                  </button>
                </div>
              ))}
            </div>

            {/* Auto-reply message */}
            {config.autoReplyEnabled && (
              <div className="mt-5 p-4 bg-[#F4FAF7] rounded-xl border border-[#1B6B3A]/20">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Auto-Reply Message Template
                </label>
                <textarea
                  value={config.autoReplyMessage}
                  onChange={(e) => setConfig({ ...config, autoReplyMessage: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 transition-colors resize-none"
                />
                <div className="text-xs text-gray-400 mt-1 text-right">{config.autoReplyMessage.length}/500</div>
              </div>
            )}
          </div>
        )}

        {/* Email Signature Section */}
        {activeSection === 'signature' && (
          <div className="bg-white rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                <i className="ri-quill-pen-line text-[#1B6B3A] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Email Signature</h3>
                <p className="text-sm text-gray-400">Appended to Gmail replies opened from the dashboard</p>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Signature Text</label>
              <textarea
                value={config.emailSignature}
                onChange={(e) => setConfig({ ...config, emailSignature: e.target.value })}
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 transition-colors resize-none font-mono"
                placeholder="Best regards,&#10;Your Name&#10;Company Name&#10;Phone Number"
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-400">This signature will be included in Gmail compose windows opened from the dashboard</p>
                <span className="text-xs text-gray-400">{config.emailSignature.length}/500</span>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-[#F4FAF7] rounded-xl p-4">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <i className="ri-eye-line text-[#1B6B3A]"></i> Preview
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-sm text-gray-500 mb-3">Hi [Customer Name],</p>
                <p className="text-sm text-gray-400 italic mb-4">[Your reply message here...]</p>
                <div className="border-t border-gray-100 pt-3">
                  <pre className="text-sm text-gray-600 font-sans whitespace-pre-wrap">{config.emailSignature}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save button */}
        <div className="flex items-center justify-between bg-white rounded-xl px-6 py-4">
          <p className="text-sm text-gray-400">
            Settings are saved locally in your browser
          </p>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-[#1B6B3A] flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill"></i> Settings saved!
              </span>
            )}
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white text-sm font-semibold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-save-line"></i> Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { STORAGE_KEY as NOTIFICATION_STORAGE_KEY };
export type { NotificationConfig };
export default NotificationSettings;
